import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import type { ShownPiece } from './OutfitPieceList';
import { Check, X, Icon } from '../../../ui/Icon';

interface Props {
  piece: ShownPiece;
  readOnly: boolean;
  /** Only while the day has a moment to drop it into. */
  draggable: boolean;
  onRemove: (pieceId: string) => void;
}

/** One planned piece: packed or not, and draggable to another moment of the day. */
export default function OutfitPieceChip({ piece, readOnly, draggable, onRemove }: Props) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: piece.id,
    disabled: !draggable,
  });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const status = piece.checked ? t('packing.outfits.packed') : t('packing.outfits.notPacked');

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...(draggable ? { ...attributes, ...listeners } : {})}
      className={`group inline-flex items-center gap-1 text-xs pl-2 pr-1 py-1 rounded-lg border ${
        piece.checked
          ? 'border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200'
      } ${draggable ? 'cursor-grab touch-none' : ''} ${isDragging ? 'relative z-20 shadow-lg cursor-grabbing' : ''}`}
      title={draggable ? `${status} · ${t('packing.outfits.dragHint')}` : status}
    >
      {piece.checked && <Icon as={Check} size={11} />}
      {piece.text}
      {!readOnly && (
        <button
          onClick={() => onRemove(piece.id)}
          className="w-5 h-5 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/25 cursor-pointer"
          aria-label={t('packing.outfits.removePiece', { text: piece.text })}
        >
          <Icon as={X} size={11} />
        </button>
      )}
    </li>
  );
}
